import {
  JEWEL_ATTACK_SPEED_PER_LEVEL_BPS,
  type PvpParticipantId,
  type PvpTeam,
} from '@kenocyber/shared';
import { PROC_LEVEL_CAP } from '../damage/proc-lottery';
import {
  PVP_FIRE_INTERVAL_TICKS,
  PVP_MISSILE_LIFETIME_TICKS,
  PVP_MISSILE_TURN_COS,
  PVP_MISSILE_TURN_SIN,
} from './balance';
import { PVP_MILLI_TICKS_PER_TICK } from './combat-profile';
import { divTrunc, type IntVector, isqrt, normalise, VECTOR_SCALE } from './vectors';

/**
 * **O míssil do duelo: a entidade, o teto e a viragem inteira.**
 *
 * ## Porque é uma entidade e não um campo num projétil
 *
 * Porque tudo o que o define contradiz o que um projétil de duelo é. Um tiro
 * comum voa em linha reta, morre em três segundos, não conhece alvo nenhum e
 * nunca reaquire - `stepProjectile` diz as quatro coisas. O míssil persegue,
 * vive nove segundos, guarda um alvo e **troca de alvo quando ele morre**.
 *
 * Enfiar isso num `PvpParticipantProjectileState` seria dar a todos os
 * projéteis campos que quase nenhum usa, e - pior - pôr o retarget a um `if` de
 * distância de um tiro comum. A Bomba já mostrou o padrão certo: uma entidade
 * com regras próprias sai do laço num sítio só. O míssil vai mais longe do que
 * ela porque nem sequer OCUPA um projétil - ele nasce ao lado da salva.
 *
 * A segunda consequência é o teto: os mísseis têm um limite próprio e uma
 * política de despejo própria - o contrato manda **preservar os novos** - e uma
 * lista partilhada com os tiros teria de escolher uma das duas políticas.
 */

/* -------------------------------------------------------------------------- */
/*  A entidade                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Um míssil vivo no duelo.
 *
 * Tudo o que decide o FUTURO dele está aqui e nada mais está: posição,
 * velocidade (a direção; a magnitude é uma constante), alvo, tempo restante e
 * identidade. É exatamente a lista que o contrato manda sobreviver a uma
 * instantânea, a uma reconexão e a uma retoma, e é essa correspondência que faz
 * a instantânea ser uma transcrição em vez de uma escolha.
 *
 * Nunca carrega conta, skin, socket, nível, cópias nem nada visual: ele
 * atravessa o fio para seis clientes.
 */
export interface PvpMissileState {
  readonly id: number;
  readonly ownerParticipantId: PvpParticipantId;
  /** Redundante com o dono, e carregado na mesma: a colisão pergunta por sub-passo. */
  readonly ownerTeam: PvpTeam;
  /**
   * O dano deste míssil, em MILLI, congelado quando a salva o lançou.
   *
   * Noventa por cento do dano-base autoritativo do casco naquele instante, e
   * nunca recalculado. Nove segundos são tempo de sobra para o perfil mudar, e
   * um míssil que perguntasse ao perfil no impacto mudaria de ideias em voo -
   * exatamente o que a fronteira temporal do V28F.B eliminou.
   */
  readonly damageMilli: bigint;
  /** A entropia congelada com a seleção. Decisões internas, nunca uma rolagem nova. */
  readonly entropy: number;
  /**
   * Quem ele persegue AGORA.
   *
   * Mutável, porque o retarget é a regra: quando o alvo morre ou desaparece, o
   * míssil escolhe imediatamente outro inimigo vivo. `null` nunca sobrevive a um
   * tique - um míssil sem alvo é um míssil que já não tem para onde ir, e o
   * contrato manda explodi-lo nesse instante.
   */
  targetParticipantId: PvpParticipantId | null;
  x: number;
  y: number;
  /** A direção, normalizada a {@link VECTOR_SCALE}. A magnitude é constante. */
  dirX: number;
  dirY: number;
  /** Tiques que faltam. Chegar a zero é explodir sem dano. */
  life: number;
  /**
   * **A LINHAGEM deste míssil: nasceu de uma reflexão, ou não** (gate V28G.O).
   *
   * Verdadeiro para o míssil EQUIVALENTE que nasce na nave refletora quando o
   * original é intercetado. Ele tem a mesma arte, a mesma chama, o mesmo
   * tamanho, a mesma velocidade, o mesmo dano congelado e os mesmos nove
   * segundos - o que muda é o dono, o alvo inicial e este campo.
   *
   * ## O que ele desliga
   *
   * A explosão dele é descendente de um payload refletido, portanto
   * `reflectionPolicy('reflected', 'missile_impact')` recusa-a - pela TABELA - e
   * não há pingue-pongue nem contra um casco que também tenha a joia. O golpe
   * entra em `applyDamage` com origem `jewel` como qualquer explosão de míssil
   * sempre entrou, o que já lhe negava proc, Roubo de Vida e estado.
   *
   * ## E o que ele NÃO muda
   *
   * Nenhum valor aprovado. Velocidade, giro, tempo de vida, raio de captura,
   * tamanho e magnitude são os mesmos, lidos das mesmas constantes: um míssil
   * refletido é o mesmo míssil a voar no outro sentido.
   */
  readonly reflected: boolean;
}

/* -------------------------------------------------------------------------- */
/*  O teto                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * O intervalo de recarga mais curto que um casco pode LEGALMENTE ter.
 *
 * A base acelerada pelo Pulso do Dragão ao nível 30 - o teto do V28G.K - com a
 * mesma aritmética exata que `pvpFireIntervalMilliTicks` usa. Derivado dos dois
 * tetos em vez de escrito, para o dia em que um deles mudar.
 */
const FASTEST_LEGAL_INTERVAL_MILLI_TICKS = Math.max(
  1,
  Math.floor(
    (PVP_FIRE_INTERVAL_TICKS * PVP_MILLI_TICKS_PER_TICK * 10_000) /
      (10_000 + JEWEL_ATTACK_SPEED_PER_LEVEL_BPS * PROC_LEVEL_CAP),
  ),
);

/** Quantos participantes um 3x3 tem. O maior dos dois rulesets. */
const MOST_PARTICIPANTS = 6;

/**
 * **O teto de mísseis simultâneos numa sala.**
 *
 * ## Derivado do PIOR CASO LEGÍTIMO, e nunca de uma chance média
 *
 * Com nove segundos de vida uma média não é um teto: o contrato exige que a sala
 * aguente **todas as salvas sucessivas a dar proc**, que é raro e perfeitamente
 * legal. A conta, com todos os fatores derivados:
 *
 * - a cadência legal mais rápida é `4 000 x 10 000 / 10 900 = 3 669` milli-tiques
 *   ({@link FASTEST_LEGAL_INTERVAL_MILLI_TICKS}), o mesmo número que a sala usa;
 * - uma oportunidade por SALVA - nunca por projétil, e é por isso que um canhão
 *   multishot não entra na conta - portanto um casco lança no máximo
 *   `floor(180 000 / 3 669) + 1 = 50` mísseis em nove segundos. O `+1` é a salva
 *   do instante zero, e esquecê-lo descontaria exatamente o míssil que nasce no
 *   tique em que o mais antigo morre;
 * - **seis** participantes, todos com a joia ao nível 30.
 *
 * `50 x 6 = 300`. Os projéteis comuns não entram porque não partilham este
 * limite: eles têm o seu (`PVP_MAX_PROJECTILES`), e um míssil nunca ocupa o lugar
 * de um tiro.
 *
 * ## O que acontece ao alcançá-lo
 *
 * Só se alcança por estado inválido, e a defesa é a que o contrato manda:
 * **preservar os mísseis novos e remover o mais antigo**. O contrário - recusar o
 * novo, como um projétil é recusado - prenderia a sala aos 300 mísseis mais
 * velhos, que são precisamente os que estão prestes a expirar sozinhos.
 */
export const PVP_MAX_MISSILES =
  (Math.floor(
    (PVP_MISSILE_LIFETIME_TICKS * PVP_MILLI_TICKS_PER_TICK) / FASTEST_LEGAL_INTERVAL_MILLI_TICKS,
  ) +
    1) *
  MOST_PARTICIPANTS;

/* -------------------------------------------------------------------------- */
/*  A viragem                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * **O raio de curvatura mínimo do míssil, e a raiz do defeito das órbitas.**
 *
 * `v / w`, com a velocidade por tique e o giro por tique:
 *
 *     37 572 / (12° em radianos) = 37 572 / 0,20944 = 179 394 mu
 *
 * Cento e oitenta mil unidades - **um vinte e sete avos da largura da arena**, e
 * cerca de sete vezes o raio de um casco. Era meio milhão enquanto o míssil voava a
 * 1,7x, e encolheu duas vezes com ele: o giro nunca mudou, portanto o raio
 * acompanha a velocidade exatamente. Um míssil mais lento vira mais apertado, e
 * os dois cortes foram a favor da correção das órbitas, nunca contra ela. É este número que faz a perseguição ingénua falhar: um
 * alvo que caia DENTRO do círculo de viragem não pode ser alcançado enquanto o
 * míssil virar sempre para o mesmo lado, e dois cascos a lutar a quatrocentas
 * mil unidades estão exatamente nessa condição.
 *
 * Escrito à mão e não calculado, pela mesma razão que o par de rotação é: uma
 * divisão por `Math.PI` aqui seria um `double` no caminho de uma decisão de
 * simulação. `missile-trajectory.test.ts` refaz a conta e compara.
 *
 * Não é uma constante de equilíbrio: ninguém a escolhe. Ela é a CONSEQUÊNCIA da
 * velocidade e do giro, que o contrato congelou, e existe nomeada para a
 * correção poder falar dela.
 */
export const PVP_MISSILE_TURN_RADIUS_MU = 179_394;

/**
 * **Roda uma direção até 12 graus na direção desejada, em inteiros.**
 *
 * ## O algoritmo, e porque não tem tolerância nenhuma
 *
 * O sentido da viragem é o SINAL do produto externo `dir x desejada`, que é uma
 * comparação de inteiros. A rotação de um passo é a matriz `[cos, -sin; sin,
 * cos]` com o par escrito em `balance.ts`, e o resultado é renormalizado para
 * {@link VECTOR_SCALE} - a matriz cresce o vetor em cerca de três milionésimos
 * por passo, e cento e oitenta passos acumulariam se ninguém o corrigisse.
 *
 * O caso delicado é o ângulo que falta ser MENOR do que um passo: rodar passaria
 * do alvo, e o míssil ficaria a oscilar. Deteta-se pela **troca de sinal do
 * produto externo** depois de rodar - exata, inteira, sem epsilon - e nesse caso
 * a direção assenta exatamente na desejada.
 *
 * ## O caso de 180 graus
 *
 * Produto externo zero com produto interno negativo: o alvo está exatamente
 * atrás. Não há sentido "mais curto", e escolher um é obrigatório - escolhe-se o
 * positivo, sempre, porque uma escolha determinística é o requisito e qualquer
 * outra regra seria a mesma escolha com mais palavras.
 *
 * @param direction a direção atual, normalizada a {@link VECTOR_SCALE}
 * @param desired a direção pretendida, normalizada a {@link VECTOR_SCALE}
 */
export function pvpRotateStep(direction: IntVector, sign: 1 | -1): IntVector {
  const sin = PVP_MISSILE_TURN_SIN * sign;
  const turned = normalise(
    divTrunc(direction.x * PVP_MISSILE_TURN_COS - direction.y * sin, VECTOR_SCALE),
    divTrunc(direction.x * sin + direction.y * PVP_MISSILE_TURN_COS, VECTOR_SCALE),
  );
  /*
   * Inalcançável: uma rotação de um vetor unitário não pode dar o vetor nulo.
   * Afirmado devolvendo a direção anterior em vez de inventar uma - um míssil
   * que apontasse para leste por causa de uma degenerescência seria pior do que
   * um míssil que manteve o rumo.
   */
  return turned ?? direction;
}

export function pvpTurnTowards(direction: IntVector, desired: IntVector): IntVector {
  const cross = direction.x * desired.y - direction.y * desired.x;
  const dot = direction.x * desired.x + direction.y * desired.y;
  if (cross === 0 && dot >= 0) {
    /* Já está alinhada. Rodar seria afastá-la do alvo. */
    return direction;
  }
  /* Zero com produto interno negativo é meia volta: o sentido é uma escolha. */
  const sign = cross === 0 ? 1 : cross > 0 ? 1 : -1;
  const turned = pvpRotateStep(direction, sign);
  const after = turned.x * desired.y - turned.y * desired.x;
  if (cross !== 0 && (cross > 0 ? after < 0 : after > 0)) {
    /* Passou do alvo: o que faltava era menos do que um passo. Assenta nele. */
    return desired;
  }
  return turned;
}

/* -------------------------------------------------------------------------- */
/*  A interceptação prevista, e a regra que mata a órbita (gate V28G.L.1)     */
/* -------------------------------------------------------------------------- */

/**
 * **Para onde apontar: o ponto de INTERCEPTAÇÃO, e não onde o alvo está.**
 *
 * Perseguir a posição atual é perseguir o passado: a cada tique o míssil aponta
 * para onde o casco esteve, chega atrasado, e acaba a arrastar-se atrás dele
 * numa curva que nunca fecha. Apontar para onde ele VAI ESTAR fecha a curva.
 *
 * ## Duas iterações, e porquê exatamente duas
 *
 * O tempo até ao encontro depende da distância, e a distância depende do tempo -
 * é implícito. Resolver a quadrática exata precisaria de uma raiz sobre produtos
 * de coordenadas da arena, que estoiram o inteiro seguro. Duas passagens do
 * ponto fixo - estimar o tempo pela distância atual, mover o alvo, reestimar -
 * chegam a menos de um por cento do encontro real para tudo o que um casco
 * consegue fazer, porque um casco anda a 17 000 por tique e o míssil a 105 400.
 *
 * Um alvo mais rápido do que o míssil divergiria; nenhum existe, e o segundo
 * passo é limitado ao tempo de vida por segurança em vez de por esperança.
 *
 * Tudo inteiro: multiplicação, soma e uma divisão truncada por passagem.
 */
export function pvpMissileAimPoint(
  from: IntVector,
  target: IntVector,
  targetVelocity: IntVector,
  speedPerTick: number,
  lifeTicks: number,
): IntVector {
  let aim = target;
  for (let pass = 0; pass < 2; pass += 1) {
    const dx = aim.x - from.x;
    const dy = aim.y - from.y;
    const distance = isqrt(dx * dx + dy * dy);
    const ticks = Math.min(lifeTicks, divTrunc(distance, speedPerTick));
    aim = {
      x: target.x + targetVelocity.x * ticks,
      y: target.y + targetVelocity.y * ticks,
    };
  }
  return aim;
}

/**
 * **Se o ponto de mira está DENTRO do círculo de viragem, ele é inalcançável.**
 *
 * ## A geometria, que é toda a explicação das órbitas
 *
 * Um míssil saturado no limite de giro percorre uma circunferência de raio
 * {@link PVP_MISSILE_TURN_RADIUS_MU}, centrada a esse raio perpendicularmente à
 * sua velocidade, do lado para onde vira. A distância entre o alvo e essa
 * circunferência é `| |C - T| - R |` - e se o alvo estiver **dentro** dela, essa
 * distância é `R - |C - T|`, que nunca desce abaixo dela por mais voltas que o
 * míssil dê.
 *
 * É por isso que ele orbitava: não lhe faltava vontade, faltava-lhe raio. Com
 * meio milhão de unidades de raio mínimo e sessenta mil de captura, um casco a
 * quatrocentas mil unidades cai exatamente lá dentro.
 *
 * @returns verdadeiro quando virar para lá NUNCA chega ao alvo
 */
export function pvpAimIsInsideTurn(
  from: IntVector,
  direction: IntVector,
  aim: IntVector,
  captureRadius: number,
): boolean {
  const cross = direction.x * (aim.y - from.y) - direction.y * (aim.x - from.x);
  if (cross === 0) {
    /* Em cima do rumo: à frente ou atrás, e nenhum dos dois está num círculo. */
    return false;
  }
  /*
   * A normal do lado para onde ele viraria, à escala do vetor. O sinal do
   * produto externo escolhe o lado, e mais nada precisa de ser decidido.
   */
  const sign = cross > 0 ? 1 : -1;
  const normalX = -direction.y * sign;
  const normalY = direction.x * sign;
  const centreX = from.x + divTrunc(normalX * PVP_MISSILE_TURN_RADIUS_MU, VECTOR_SCALE);
  const centreY = from.y + divTrunc(normalY * PVP_MISSILE_TURN_RADIUS_MU, VECTOR_SCALE);
  const dx = aim.x - centreX;
  const dy = aim.y - centreY;
  const reach = PVP_MISSILE_TURN_RADIUS_MU - captureRadius;
  if (reach <= 0) {
    return false;
  }
  return dx * dx + dy * dy < reach * reach;
}

/**
 * **A direção do próximo tique.** Persegue, ou passa ao lado para poder voltar.
 *
 * ## As duas regras, e a segunda é a correção inteira
 *
 * 1. o ponto de mira é alcançável — vira para ele, até 12 graus, exatamente como
 *    sempre virou;
 * 2. o ponto de mira está **dentro** do círculo de viragem — **segue em frente**.
 *
 * A segunda não é um privilégio: o giro continua limitado a 240 graus por
 * segundo e a velocidade continua a mesma. O que ela faz é deixar de gastar o
 * giro numa curva que, por geometria, nunca fecha. Voando a direito, a distância
 * ao alvo cresce; quando ela passa `2R` o alvo deixa de poder estar dentro de
 * qualquer círculo de viragem, e a regra 1 volta a valer sozinha.
 *
 * É o reataque de um míssil verdadeiro, e sai da mesma aritmética: nada
 * teleporta, nada vira instantaneamente, e a trajetória continua a ser um
 * resultado da simulação.
 */
export function pvpMissileSteer(
  direction: IntVector,
  from: IntVector,
  aim: IntVector,
  captureRadius: number,
): IntVector {
  if (pvpAimIsInsideTurn(from, direction, aim, captureRadius)) {
    /*
     * **A fuga é virar AO CONTRÁRIO, e não seguir em frente.**
     *
     * As duas abrem a geometria, e só uma cabe na arena. A direito, o míssil
     * precisa de percorrer perto de um milhão de unidades para o alvo sair do
     * círculo de viragem, e mais outro tanto para dar meia volta - numa caixa de
     * 4 800 000 por 3 000 000 ele bate no limite primeiro, e a correção da órbita
     * passava a ser uma fuga para fora do mapa.
     *
     * Virando para o outro lado ele descreve o círculo OPOSTO, que tem o alvo do
     * lado de fora: a distância a ele oscila entre `|C - T| - R` e `|C - T| + R`,
     * portanto num quarto de volta já está longe o suficiente para o círculo
     * natural deixar de o conter - e a partir daí a perseguição fecha sozinha.
     * O míssil fica contido num laço de cerca de um milhão de unidades em vez de
     * partir em linha reta.
     *
     * É o reataque de um míssil verdadeiro. O giro continua limitado a 240 graus
     * por segundo e a velocidade continua a mesma: o que muda é o SINAL.
     */
    const cross = direction.x * (aim.y - from.y) - direction.y * (aim.x - from.x);
    return pvpRotateStep(direction, cross > 0 ? -1 : 1);
  }
  const desired = normalise(aim.x - from.x, aim.y - from.y);
  return desired === null ? direction : pvpTurnTowards(direction, desired);
}

/* -------------------------------------------------------------------------- */
/*  A colisão VARRIDA                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Uma fração de um tique, como par de inteiros. Nunca um `double`.
 *
 * Comparada por multiplicação cruzada - `a.num * b.den` contra `b.num * a.den` -
 * o que mantém a ordem exata sem nunca dividir. Os dois denominadores são
 * positivos por construção.
 */
export interface PvpSweepHit {
  readonly num: number;
  readonly den: number;
}

/** Qual das duas frações é a mais cedo. Exata, e sem divisão. */
export function pvpSweepEarlier(left: PvpSweepHit, right: PvpSweepHit): boolean {
  return left.num * right.den < right.num * left.den;
}

/**
 * **Quanto do passo o míssil percorre antes de tocar um círculo.**
 *
 * O teste que substitui a amostragem por sub-passos. Um míssil anda 105 400
 * unidades por tique contra uma captura de 61 525: quatro amostras chegavam por
 * pouco, e deixavam de chegar assim que a correção da órbita o pôs a atacar de
 * frente e à velocidade máxima. Um segmento não tem buracos.
 *
 * ## Porque a aritmética é reduzida antes de elevar ao quadrado
 *
 * Porque `b²` sobre coordenadas de arena chega a `10^20` e o inteiro seguro
 * acaba em `9 x 10^15`. O segmento e o vetor até ao centro são divididos por
 * {@link SWEEP_UNIT} - só depois de uma saída rápida garantir que estão perto -
 * e a quadrática resolve-se em números pequenos com {@link isqrt}, que é exato.
 * A fração devolvida é aplicada ao passo por INTEIRO, portanto a redução decide
 * o instante e nunca a posição.
 *
 * @returns a fração do passo, ou `null` quando o segmento não toca o círculo
 */
export function pvpSweepCircle(
  from: IntVector,
  step: IntVector,
  centre: IntVector,
  radius: number,
): PvpSweepHit | null {
  const fx = from.x - centre.x;
  const fy = from.y - centre.y;
  /*
   * Saída rápida, e é ela que torna a redução segura: se o centro está mais
   * longe do que o passo inteiro mais o raio, nenhum ponto do segmento lá chega.
   */
  const span = isqrt(step.x * step.x + step.y * step.y) + radius;
  if (Math.abs(fx) > span || Math.abs(fy) > span) {
    return null;
  }
  if (fx * fx + fy * fy <= radius * radius) {
    /* Já em contacto no início do passo. O toque é agora. */
    return { num: 0, den: 1 };
  }
  const sx = divTrunc(step.x, SWEEP_UNIT);
  const sy = divTrunc(step.y, SWEEP_UNIT);
  const gx = divTrunc(fx, SWEEP_UNIT);
  const gy = divTrunc(fy, SWEEP_UNIT);
  const r = divTrunc(radius, SWEEP_UNIT);
  const a = sx * sx + sy * sy;
  if (a === 0) {
    return null;
  }
  const b = gx * sx + gy * sy;
  const c = gx * gx + gy * gy - r * r;
  const discriminant = b * b - a * c;
  if (discriminant < 0) {
    return null;
  }
  const root = isqrt(discriminant);
  const num = -b - root;
  if (num < 0 || num > a) {
    return null;
  }
  return { num, den: a };
}

/**
 * Quanto do passo o míssil percorre antes de entrar numa caixa.
 *
 * O método das faixas, com as duas frações comparadas por multiplicação cruzada
 * em vez de divididas. A caixa é a do obstáculo, alargada pelo CORPO do míssil -
 * é o corpo que toca a pilastra, e não a caixa de captura de um casco.
 */
export function pvpSweepBox(
  from: IntVector,
  step: IntVector,
  box: { readonly x: number; readonly y: number; readonly w: number; readonly h: number },
  margin: number,
): PvpSweepHit | null {
  const lowX = box.x - margin;
  const highX = box.x + box.w + margin;
  const lowY = box.y - margin;
  const highY = box.y + box.h + margin;

  /* A janela de tempo em que o segmento está dentro de cada faixa. */
  let enterNum = 0;
  let enterDen = 1;
  let exitNum = 1;
  let exitDen = 1;

  const axis = (origin: number, delta: number, low: number, high: number): boolean => {
    if (delta === 0) {
      return origin >= low && origin <= high;
    }
    const first = delta > 0 ? low - origin : high - origin;
    const second = delta > 0 ? high - origin : low - origin;
    const den = delta > 0 ? delta : -delta;
    const firstNum = delta > 0 ? first : -first;
    const secondNum = delta > 0 ? second : -second;
    if (firstNum * enterDen > enterNum * den) {
      enterNum = firstNum;
      enterDen = den;
    }
    if (secondNum * exitDen < exitNum * den) {
      exitNum = secondNum;
      exitDen = den;
    }
    return true;
  };

  if (!axis(from.x, step.x, lowX, highX)) {
    return null;
  }
  if (!axis(from.y, step.y, lowY, highY)) {
    return null;
  }
  if (enterNum < 0) {
    enterNum = 0;
    enterDen = 1;
  }
  /* Sem sobreposição das duas janelas, ou já passou: não há toque neste passo. */
  if (enterNum * exitDen > exitNum * enterDen) {
    return null;
  }
  if (enterNum > enterDen || exitNum < 0) {
    return null;
  }
  return { num: enterNum, den: enterDen };
}

/**
 * Quanto do passo o míssil percorre antes de sair da arena.
 *
 * A mesma aritmética das faixas, ao contrário: o instante em que ele DEIXA de
 * estar dentro da caixa do mundo.
 */
export function pvpSweepBounds(
  from: IntVector,
  step: IntVector,
  width: number,
  height: number,
): PvpSweepHit | null {
  let exitNum = 1;
  let exitDen = 1;
  let bounded = false;
  const axis = (origin: number, delta: number, high: number): void => {
    if (delta === 0) {
      return;
    }
    const limit = delta > 0 ? high - origin : origin;
    const den = delta > 0 ? delta : -delta;
    if (limit < 0) {
      /* Já fora no início do passo. */
      exitNum = 0;
      exitDen = 1;
      bounded = true;
      return;
    }
    if (limit * exitDen < exitNum * den) {
      exitNum = limit;
      exitDen = den;
      bounded = true;
    }
  };
  axis(from.x, step.x, width);
  axis(from.y, step.y, height);
  if (!bounded || exitNum * 1 >= exitDen * 1) {
    return null;
  }
  return { num: exitNum, den: exitDen };
}

/** Onde o míssil fica quando o passo é cortado nesta fração. Inteiro e exato. */
export function pvpSweepPoint(from: IntVector, step: IntVector, at: PvpSweepHit): IntVector {
  return {
    x: from.x + divTrunc(step.x * at.num, at.den),
    y: from.y + divTrunc(step.y * at.num, at.den),
  };
}

/**
 * A redução aplicada antes de elevar ao quadrado, e porque é 64.
 *
 * Sessenta e quatro põe `b²` e `a·c` abaixo de `10^14` para qualquer passo e
 * qualquer raio que esta arena produza, com folga de duas ordens de grandeza
 * face ao inteiro seguro. O que ela custa é resolução no INSTANTE do toque -
 * cerca de sessenta e quatro unidades de mundo, seis centésimos de uma unidade
 * de arena - e nada na posição, que é calculada com o passo por inteiro.
 */
const SWEEP_UNIT = 64;
